import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import deleteBlackIcon from '../../../../assets/img/svg/delete-black.svg';
import { Link } from 'react-router-dom';
import CustomSpinner from '../../../ReusableComponents/CustomSpinner';
import { useDispatch, useSelector } from 'react-redux';
import { createCalendarTask, deleteCalendarTask, viewalendarTask } from '../../../../redux/Slices/calendar/CalendarSlice';
import { useForm } from 'react-hook-form';
import SubmitSpinnerButton from '../../../ReusableComponents/SubmitSpinnerButton';
import ConfirmApproval from '../confirm-approval/ConfirmApproval';
import { showSuccessOrError } from '../../../../services/commonService';
import translation from '../../../../utils/translation';
import configData from '../../../ReusableComponents/app-configs';

function ViewUpdateTaskCalendar({ onClose, taskId, onRefresh }) {
  const dispatch = useDispatch();
  const [viewMode, setViewMode] = useState(true);
  const { taskDetail } = useSelector((state) => state.calendar);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [isConfirmDelete, setIsConfirmDelete] = useState(false);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: configData.viewUpdateTaskFormState
  });

  const toggleEditMode = () => {
    setViewMode(!viewMode);
  };

  const onDeleteClick = (e) => {
    e && e.preventDefault();
    setIsConfirmDelete(true);
  };

  const handleDeleteConfirmation = () => {
    console.log('Delete task with ID:', taskDetail.id);
    dispatch(deleteCalendarTask({ task_id: taskDetail.id})).then((res) => {
      if(!res.error) {
        setIsConfirmDelete(false);
        if (onRefresh) {
          onRefresh();
        }
        onClose();
      }
    });
  };

  const formatDateTime = (date, time) => {
    if (!date) return '';
    
    const formattedDate = new Date(date);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    
    const dayName = days[formattedDate.getDay()];
    const monthName = months[formattedDate.getMonth()];
    const day = formattedDate.getDate();
    
    // Format time if provided
    let timeStr = '';
    if (time) {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'pm' : 'am';
      const hour12 = hour % 12 || 12;
      timeStr = ` - ${hour12}:${minutes} ${ampm}`;
    }
    
    return `${dayName}, ${monthName} ${day}${timeStr}`;
  };

  const onSubmit = (data) => {
    setSubmitStatus('loading');
    console.log("Form submitted with data:", data);
    const taskTime = data.task_time
      ? (data.task_time.includes(':') && !data.task_time.endsWith(':00')
        ? `${data.task_time}:00`
        : data.task_time)
      : null;

    // Prepare payload for saving
    const payload = {
      id: taskDetail.id,
      task_name: data.task_name,
      task_description: data.task_description,
      task_date: data.task_date,
      task_time: taskTime,
      // status: taskDetail.status
    };
    console.log("Payload to save:", payload);
    dispatch(createCalendarTask(payload)).then((res) => {
      showSuccessOrError(res);
      setSubmitStatus('success');
      if(!res.error) {
        setViewMode(true);
        if (onRefresh) {
          onRefresh();
        }
      }
    })
  };

  useEffect(() => {
    setIsLoadingContent(true);
    dispatch(viewalendarTask(taskId)).then(() => {
      setIsLoadingContent(false);
    });
  }, [dispatch, taskId]);

  useEffect(() => {
    if (taskDetail) {
      setValue('task_name', taskDetail.task_name);
      setValue('task_description', taskDetail.task_description);
      setValue('task_date', taskDetail.task_date);
      setValue('task_time', taskDetail.task_time);
    }
  }, [taskDetail, setValue]);

  return (
    <React.Fragment>
      {isConfirmDelete ? (
        <ConfirmApproval 
          description={translation('key_are_you_sure_delete_task')}
          onClose={() => setIsConfirmDelete(false)} 
          handleConfirmation={handleDeleteConfirmation} 
        />
      ) : (
        <div
          className="modal fade modalCustom viewUpdateTask show"
          tabIndex={-1}
          aria-labelledby="exampleModalCenterTitle"
          style={{
            display: "block",
            background: "rgba(0, 0, 0, 0.7)",
          }}
          aria-modal="true"
          role="dialog"
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content br20">
              <div className="modal-body p32">
                <div className="d-flex justify-content-between align-items-center mb24">
                  <h5 className="fz20 font-roboto fw-500 black">
                    {viewMode ? taskDetail?.task_name || 'Task Name' : 'Edit Task'}
                    {viewMode && taskDetail && (
                      <p className="m-0 fz16 fw-400 mt-1">
                        {formatDateTime(taskDetail.task_date, taskDetail.task_time)}
                      </p>
                    )}
                  </h5>
                  <div className="d-flex align-items-center">
                    {viewMode && (
                      <>
                        <Link to="#" onClick={onDeleteClick}>
                          <img src={deleteBlackIcon} alt="delete" className="cursor-pointer mr15" />
                        </Link>
                        <i 
                          className="ri-pencil-fill mr-2 cursor-pointer blue fz20"
                          onClick={toggleEditMode}
                        ></i>
                      </>
                    )}
                    <i className="ri-close-line cursor-pointer grey2 fz20" onClick={onClose}></i>
                  </div>
                </div>
                {isLoadingContent ? (
                  <CustomSpinner />
                ) : (
                  <>
                    {viewMode ? (
                      <p>{taskDetail?.task_description || 'No description available.'}</p>
                    ) : (
                      <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="form-group mb-3">
                          <label className="form-label required">{translation('key_task_name')}</label>
                          <input 
                            type="text" 
                            className={`form-control ${errors.task_name ? 'is-invalid' : ''}`} 
                            {...register("task_name", { required: translation('key_task_name_is_required') })}
                          />
                          {errors.task_name && (
                            <div className="invalid-feedback">
                              {errors.task_name.message}
                            </div>
                          )}
                        </div>
                        
                        <div className="form-group mb-4">
                          <label className="form-label required">{translation('key_description')}</label>
                          <textarea
                            className={`form-control ${errors.task_description ? 'is-invalid' : ''}`}
                            rows="3"
                            {...register("task_description", { required: translation('key_this_field_is_required') })}
                          ></textarea>
                          {errors.task_description && (
                            <div className="invalid-feedback">
                              {errors.task_description.message}
                            </div>
                          )}
                        </div>
                        
                        <div className="row mb-3">
                          <div className="col">
                            <div className="form-group">
                                    <label className="form-label required">{ translation('key_date') }</label>
                              <input
                                type="date"
                                className={`form-control ${errors.task_date ? 'is-invalid' : ''}`}
                                {...register("task_date", { required: "Date is required" })}
                              />
                              {errors.task_date && (
                                <div className="invalid-feedback">
                                  {errors.task_date.message}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col">
                            <div className="form-group">
                              <label className="form-label required">{translation('key_time')}</label>
                              <input
                                type="time"
                                className={`form-control ${errors.task_time ? 'is-invalid' : ''}`}
                                {...register("task_time", { required: translation('key_time_is_required') })}
                              />
                              {errors.task_time && (
                                <div className="invalid-feedback">
                                  {errors.task_time.message}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className='row'>
                          <div className='col-12 footer-btns text-right'>
                            <button 
                              type="button" 
                              className="btn btn-outline mr-2" 
                              onClick={() => setViewMode(true)}
                            >
                              {translation('key_cancel')}
                            </button>
                            <SubmitSpinnerButton status={submitStatus} submitText={translation('key_save')} />
                          </div>
                        </div>
                      </form>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

ViewUpdateTaskCalendar.propTypes = {
  onClose: PropTypes.func.isRequired,
  taskId: PropTypes.number,
  onRefresh: PropTypes.func
};

export default ViewUpdateTaskCalendar;